import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo } from 'react';

import {
  BaseAuthedPage,
  MaxWidth,
  ModelIndexSimple,
  ModelIndexTable,
  ModelSearch,
  ModelFilters,
  ModelPager,
  ModelCell
} from '@rhino-project/ui-heroui';
import {
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useExternalPlayerAdd } from '../../hooks/externalplayer';

type MemberAction = {
  name: number | string;
  label: string;
  onAction: (teamSeason: string | number, playerId?: string | number) => void;
};

type DatabasePlayerActionsDropdownProps = {
  memberActions: MemberAction[];
  onAction: (name: number | string) => void;
};

export const DatabasePlayerActionsDropdown = ({
  memberActions,
  onAction
}: DatabasePlayerActionsDropdownProps) => {
  const { t } = useTranslation();

  return (
    <Dropdown>
      <DropdownTrigger className="button--ghost min-w-0 px-1 py-0 h-auto">
        <Icon icon="bi:three-dots-vertical" width="18" height="18" />
      </DropdownTrigger>
      <DropdownPopover>
        <DropdownMenu aria-label={t('frontend.messages.player_actions')}>
          {memberActions.map((a) => (
            <DropdownItem
              key={a.name}
              id={a.name}
              textValue={a.label}
              onAction={() => onAction(a.name)}
            >
              {a.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </DropdownPopover>
    </Dropdown>
  );
};

type TableRow = {
  original: {
    id: number | string;
    [key: string]: unknown;
  };
};

type DatabasePlayerTableActionsProps = {
  memberActions: MemberAction[];
  // row is injected by ModelIndexTable at render time via cloneElement
  row?: TableRow;
  // Extra props passed when used as a table column component
  id?: string;
  header?: string;
};

// Table-row wrapper that extracts the external_player id from the row
const DatabasePlayerTableActions = ({
  memberActions,
  row
}: DatabasePlayerTableActionsProps) => {
  const { handleAdd } = useExternalPlayerAdd();
  if (!row) return null;
  return (
    <DatabasePlayerActionsDropdown
      memberActions={memberActions}
      onAction={(teamSeason) => handleAdd(teamSeason, row.original.id)}
    />
  );
};

// Column ids are the path strings (or the element's path/id) from tablePaths
const tableColumnConfig = {
  last_name: { width: 1.5 },
  first_name: { width: 1.5 },
  dob_year: { width: 1, hideBelow: 'sm' },
  grad_year: { width: 1, hideBelow: 'md' },
  position: { width: 1 },
  'external_rosters.0.external_team_season.external_team': {
    width: 2,
    hideBelow: 'md'
  },
  commitment: { width: 2, hideBelow: 'lg' },
  actions: { width: 0.5 }
} as const;

const DatabasePage = () => {
  const { t } = useTranslation();

  const { memberActions } = useExternalPlayerAdd();

  const tablePaths = useMemo(() => {
    return [
      'last_name',
      'first_name',
      'dob_year',
      'grad_year',
      'position',
      // eslint-disable-next-line @eslint-react/no-missing-key
      <ModelCell
        path="external_rosters.0.external_team_season.external_team"
        header={t('frontend.messages.most_recent_team')}
      />,
      'commitment',
      memberActions ? (
        // eslint-disable-next-line @eslint-react/no-missing-key
        <DatabasePlayerTableActions
          memberActions={memberActions}
          id="actions"
          header=""
        />
      ) : null
    ].filter(Boolean);
  }, [memberActions, t]);

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <ModelIndexSimple
          analyticsSurface="index_page"
          model="external_player"
          syncUrl
        >
          <div className="flex flex-col gap-3">
            <ModelSearch />
            <ModelFilters paths={['position', 'dob_year', 'grad_year']} />
            <div className="flex flex-row">
              <div className="ml-auto">
                <ModelPager />
              </div>
            </div>
            <hr />
            <ModelIndexTable
              paths={tablePaths}
              columnConfig={tableColumnConfig}
            />
          </div>
        </ModelIndexSimple>
      </MaxWidth>
    </BaseAuthedPage>
  );
};

export default DatabasePage;
